/**
 * Tutor detail collapse.
 * Header (name + status / situation badges) plus tabbed sections; hydrates from full state.
 */
import * as React from 'react';
import { User, CheckCircle2, Clock, BookOpen } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { UserHistoryTimeline } from '@/components/common/details/UserHistoryTimeline';
import { useSessionFormData } from '@/hooks/useSessionFormData';
import { useDepartments } from '@/hooks/useDepartments';
import type { FullState, TutorRecord } from '@/types/remote/tutor.types';
import {
  TUTOR_PENDING_STATUS_CONFIG,
  TUTOR_ACTIVE_STATUS_CONFIG,
  TUTOR_DETAIL_TAB_CONFIGS,
  type TutorDetailTab,
  TUTOR_SITUATION_CONFIG,
  ASSIGNABILITY_CONFIG,
} from '@/components/tutors/tutor-configs';
import {
  formatProgramsText,
  getAccompaniedStudents,
  getTutorRemoteBusinessStatus as getTutorBusinessStatus,
  getTutorSituation,
} from './tutor-table-utils';
import { TutorDetailsTab } from './tutor-detail-tabs/TutorDetailsTab';
import { TutorStudentsTab } from './tutor-detail-tabs/TutorStudentsTab';
import { TutorRegistrationTab } from './tutor-detail-tabs/TutorRegistrationTab';
import { TutorInfoSessionTab } from './tutor-detail-tabs/TutorInfoSessionTab';
import { TutorDocumentsTab } from './tutor-detail-tabs/TutorDocumentsTab';

/** Props for {@link TutorDetails}. */
interface Props {
  record: TutorRecord;
  /** Full-state extras (templates / steps) when the {@link DataTable} already fetched GET full-state. */
  fullState?: FullState;
  onEditSection?: (stepKey: string) => void;
}

/** Active tab on the remote tutor details panel. */
type TutorTab = TutorDetailTab;

/** Tutor detail collapse. */
export const TutorDetails: React.FC<Props> = ({ record, fullState, onEditSection }) => {
  const [activeTab, setActiveTab] = React.useState<TutorTab>('details');
  const { data: departments = [] } = useDepartments('FR');
  const { programs } = useSessionFormData();
  const displayRecord = record;

  const { tab, status } = getTutorBusinessStatus({
    onboardingStatus: displayRecord.onboardingStatus,
    folderSituation: displayRecord.folderSituation,
    tutorSituation: displayRecord.tutorSituation,
    user: displayRecord.user,
  });

  const statusConfig =
    tab === 'pre_registered'
      ? TUTOR_PENDING_STATUS_CONFIG[status as keyof typeof TUTOR_PENDING_STATUS_CONFIG]
      : TUTOR_ACTIVE_STATUS_CONFIG[status as keyof typeof TUTOR_ACTIVE_STATUS_CONFIG];

  const situationKey = getTutorSituation({
    onboardingStatus: displayRecord.onboardingStatus,
    tutorSituation: displayRecord.tutorSituation,
  });
  const situationConfig = TUTOR_SITUATION_CONFIG[situationKey] ?? {
    label: situationKey,
    icon: Clock,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--status-pending))',
  };
  const SituationIcon = situationConfig.icon;

  const assignabilityConfig =
    ASSIGNABILITY_CONFIG[displayRecord.affectability === 'available' ? 'available' : 'unavailable'];

  const sections = displayRecord.sections ?? {};
  const completion = displayRecord.completion;
  const accompaniedStudents = getAccompaniedStudents(displayRecord);
  const matchedStudentsCount = displayRecord.matchedStudents?.length ?? 0;

  const programInfo = (sections['my_support'] ?? sections['program_data']) as any;
  const programText = formatProgramsText(programInfo, programs);

  const tabs: TabItem<TutorTab>[] = TUTOR_DETAIL_TAB_CONFIGS.map(({ id, label, icon: Icon }) => ({
    id,
    label,
    icon: <Icon className="w-3 h-3" />,
  }));

  const StatusIcon = statusConfig?.icon ?? CheckCircle2;

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">
                {displayRecord.user?.firstName ??
                  (sections['my_information'] as any)?.firstName ??
                  '—'}{' '}
                {displayRecord.user?.lastName ??
                  (sections['my_information'] as any)?.lastName ??
                  '—'}
              </h3>
              {statusConfig && (
                <LabelBadge
                  label={statusConfig.label}
                  icon={StatusIcon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              )}
              <LabelBadge
                label={situationConfig.label}
                icon={SituationIcon}
                variant={situationConfig.badgeVariant}
                color={situationConfig.badgeColor}
                size="sm"
              />
              <LabelBadge
                label={programText}
                icon={BookOpen}
                variant="outlined"
                color="rgb(var(--color-info))"
                size="sm"
              />
            </div>
            <p className="details-view-header-subtitle">
              {displayRecord.user?.email ??
                (sections['my_information'] as any)?.email ??
                'Non renseigné'}{' '}
              •{' '}
              {displayRecord.user?.phone ??
                (sections['my_information'] as any)?.phoneNumber ??
                'Non renseigné'}
            </p>
          </div>
        </div>
      </div>

      <Tabs items={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id)} />

      {activeTab === 'details' && (
        <TutorDetailsTab
          record={displayRecord}
          displayRecord={displayRecord}
          sections={sections}
          completion={completion}
          departments={departments}
          statusConfig={statusConfig}
          assignabilityConfig={assignabilityConfig}
        />
      )}

      {activeTab === 'students' && <TutorStudentsTab accompaniedStudents={accompaniedStudents} />}

      {activeTab === 'registration' && (
        <TutorRegistrationTab
          record={displayRecord}
          sections={sections}
          completion={completion}
          fullState={fullState}
          departments={departments}
          programs={programs}
        />
      )}

      {activeTab === 'info_session' && (
        <TutorInfoSessionTab
          userId={displayRecord.userId}
          sections={sections}
          onEditSection={onEditSection}
        />
      )}

      {activeTab === 'documents' && (
        <TutorDocumentsTab
          record={displayRecord}
          sections={sections}
          matchedStudentsCount={matchedStudentsCount}
        />
      )}

      {activeTab === 'history' && (
        <UserHistoryTimeline userId={displayRecord.userId} role="tutor_remote" />
      )}
    </>
  );
};

/** Factory for the {@link DataTable} expanded-row collapse. */
export const renderTutorDetails = (
  record: TutorRecord,
  onEditSection?: (stepKey: string) => void,
  fullState?: FullState,
): React.ReactNode => (
  <TutorDetails record={record} fullState={fullState} onEditSection={onEditSection} />
);
